import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { humanize, titleize, unescapeHTML } from 'underscore.string';
import moment from 'moment-timezone';
import { isDrayageOrRailShipment, isDrayageShipment, isIntermodalShipmentType, } from 'helpers/shipment-types';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import Accessorials from 'components/modules/shipments/Accessorials';
import { Badge, Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, } from '@cargomatic/ui';
import Node from 'components/modules/shipments/stops/Node';
import { getLatestStatus, shipperHasTag } from 'utils/formatters/shipments';
import { getDisplayAction } from 'utils/formatters/stops';
import CircleButton from 'components/v2/buttons/CircleButton';
import SessionStore from 'stores/SessionStore';
import DescriptionListItem from 'components/v2/DescriptionListItem';
import InfoIcon from 'components/v2/icons/InfoIcon';
import RefreshIcon from 'components/v2/icons/RefreshIcon';
import { calculatePalletSpaces } from 'helpers/shipments';
import { getTimezone } from 'helpers/stops';
import { Translation, withTranslation } from 'react-i18next';
import { sluggify } from 'utils/localization/utils';
import { getEquipmentBadgeColor, trailerExists } from 'helpers/equipment';
import { t } from 'i18next';
import { Disclosure } from '@headlessui/react';
import { Documents } from '../../documents/Documents';
import StopAppointmentWindow from './StopAppointmentWindow';
import { LoadDimensionFormatter, LoadUnitFormatter, } from '../../master/formatters';
import Tooltip from 'components/ui/Tooltip';
import { PencilIcon } from 'lucide-react';
import { Timeline, TimelineDot, TimelineEvent } from 'components/ui/Timeline';
import phoneFormatter from 'phone-formatter';
function filterTimelineItems(items) {
    const canceled = items.find((item) => item.status.slug === 'canceled');
    if (canceled) {
        return [canceled];
    }
    if (items.length > 1) {
        return items.filter((item) => item.status.slug !== 'unstarted');
    }
    return items;
}
const TOOLTIPS = {
    accessorials: (React.createElement(Tooltip, { id: "tooltip-route-details" },
        React.createElement(Translation, null, (t) => t('STOP_ACCESSORIALS_TOOLTIP')))),
    references: (React.createElement(Tooltip, { id: "tooltip-route-details" },
        React.createElement(Translation, null, (t) => t('REF_NUMS_NOTE')))),
    documents: (React.createElement(Tooltip, { id: "tooltip-route-details" },
        React.createElement(Translation, null, (t) => t('STOP_DOCUMENTS_TOOLTIP')))),
};
const getStopTooltip = (text) => {
    return React.createElement(Tooltip, { id: "tooltip-stop-audit-source" }, humanize(text));
};
const getStopName = (location) => {
    if (location && location.length > 0) {
        return unescapeHTML(location[0].name);
    }
    return null;
};
const getStopAddress = (location) => {
    let address = '';
    if (location && location.length > 0) {
        address += location[0].address;
        if (location[0].suite_or_apt) {
            address += ', Suite/Apt: ' + location[0].suite_or_apt;
        }
    }
    return address;
};
const getContact = (contact) => {
    if (!_.isEmpty(contact)) {
        const contactName = humanize(contact.name || 'Not Specified');
        if (contact.phone) {
            let phoneNumber = contact.phone.trim();
            phoneNumber = phoneNumber.replace(/\D/g, ''); // Normalizing the removal spaces and characters for formatting purposes
            if (phoneNumber.charAt(0) !== '1')
                // Normalizing adding country code when it's left off of complete number
                phoneNumber = '1' + phoneNumber;
            if (phoneNumber.length < 11) {
                // Default to the number in this cases. 11 corresponds to 1-234-567-8910 (Puncuation removed)
                return `${contactName}, ${contact.phone}`;
            }
            // Otherwise the number has an extension presumably
            const number = phoneNumber.substring(0, 11);
            const extension = phoneNumber.substring(11, phoneNumber.length);
            const formattedNumber = phoneFormatter.format(number, '(NNN) NNN-NNNN');
            if (extension) {
                return `${contactName}, ${formattedNumber} (${extension})`;
            }
            return `${contactName}, ${formattedNumber}`;
        }
        return contactName;
    }
    return null;
};
// Rename back to shouldEnterProvideDocumentation once we go live and remove old function
const shouldEnterProvideDocumentationWithCombinedDocsCheck = (stop) => {
    const { complete, missing_docs, missing_combined_docs } = stop;
    // Missing Docs from SSR should return Provide Docmentation irrespective of carrier docs
    if (featureEnabled(CONFIGURED_FEATURES.SSR) && complete && missing_docs) {
        return 'Provide documentation';
    }
    // Combined doc check
    if (featureEnabled(CONFIGURED_FEATURES.SSR) &&
        complete &&
        missing_combined_docs) {
        return 'Provide documentation';
    }
    return null;
};
const shouldEnterProvideDocumentation = (shipment, stop) => {
    const { complete, off_site, missing_docs, empty } = stop;
    // SSR - This should be simplified to just checking completion and missing docs once we go live
    if (!featureEnabled(CONFIGURED_FEATURES.SSR) &&
        complete &&
        off_site === false &&
        missing_docs &&
        !empty) {
        return 'Provide documentation';
    }
    // SSR - This should be the case used when we go live
    if (featureEnabled(CONFIGURED_FEATURES.SSR) && complete && missing_docs) {
        return 'Provide documentation';
    }
    return null;
};
const shouldAddEquipment = (shipment, stop) => {
    if (isDrayageShipment(shipment) && !stop.complete && !stop.canceled) {
        const { equipments: { length }, equipments, } = stop;
        const isWithoutEquipment = length === 0;
        let hasTrailer = false;
        let hasContainer = false;
        if (!isWithoutEquipment) {
            hasTrailer = trailerExists(equipments);
            hasContainer = _.find(equipments, (equipment) => {
                const { _equipmentType } = equipment;
                return _equipmentType === 'container';
            });
        }
        const hasEquipment = hasTrailer || hasContainer;
        return isWithoutEquipment || !hasEquipment;
    }
    return false;
};
const hasNotArrived = (latestTimelineObject, shipper, buttonState, onClick) => {
    const arrivedStatus = 40;
    const reasonRequired = shipper.requires_late_early_reason_code ||
        shipper.requires_on_time_reason_code ||
        shipper.requires_arrival_info;
    if (latestTimelineObject.status &&
        latestTimelineObject.status.code < arrivedStatus &&
        reasonRequired) {
        return (React.createElement(Button, { disabled: buttonState, onClick: () => onClick(), className: "w-full bg-green-cm hover:bg-green-cm" }, t('ARRIVED')));
    }
    return null;
};
const hasMetaDate = (timeline) => timeline.meta && timeline.meta.date;
class Stop extends Component {
    constructor(props) {
        super(props);
        this.setDocumentsRefetchRef = (documentsRefetch) => {
            this.setState({
                documentsRefetch,
            });
        };
        this.handleStopArrived = () => {
            this.props.onShowStopArrivedModal(this.props.stop);
        };
        this.handleEquipmentClick = () => {
            this.props.onShowEditEquipmentModal(this.props.stop);
        };
        this.state = {
            hoveredStop: null,
            hoveredType: null,
            documentsRefetch: null,
        };
    }
    hasShorthaulInvoicingData(props) {
        return (props.shipment &&
            props.shipment.shipment_type === 'shorthaul' &&
            (props.shipment.payout_exists === true ||
                props.shipment.invoice_exists === true));
    }
    onMouseOver(hoveredStop, hoveredType) {
        this.setState({ hoveredStop, hoveredType });
    }
    onMouseOut() {
        this.setState({ hoveredStop: null, hoveredType: null });
    }
    getLoadPreview(shipment) {
        let totalPalletSpaces;
        const { t } = this.props;
        const { loads } = this.props.stop;
        if (shipment.loads && shipment.loads.length) {
            totalPalletSpaces = calculatePalletSpaces(shipment.loads);
        }
        const previewComponent = [];
        if (this.props.stop.full_truckload) {
            previewComponent.push(React.createElement(Badge, { key: "ltl", className: "mb-4" }, t('FULL_TRUCKLOAD')));
        }
        const showTotalWeight = this.props.showTotalWeight;
        const shipmentWeightUnit = this.props.shipment.weight_units;
        if (showTotalWeight) {
            previewComponent.push(React.createElement(DescriptionListItem, { key: "weight", label: t('TOTAL_WEIGHT'), className: "mb-2" },
                React.createElement(LoadUnitFormatter, { weight: shipment.weight, weight_units: shipmentWeightUnit })));
        }
        _.each(this.props.stop.equipments, (equipment) => {
            const { _equipmentType } = equipment;
            if (!_equipmentType || !_equipmentType.length) {
                return null;
            }
            let badgeColor = getEquipmentBadgeColor(equipment);
            const badgeText = titleize(t(sluggify(_equipmentType)));
            previewComponent.push(React.createElement("div", { key: equipment._id, className: "flex items-center mb-2 space-x-2 min-w-0" },
                React.createElement(Button, { onClick: () => this.handleEquipmentClick(equipment), className: "text-blue-600 hover:text-blue-700 px-0 truncate", variant: "link" }, `${equipment.equipment_id}`),
                React.createElement(Badge, { className: "shrink-0", color: badgeColor }, badgeText)));
        });
        // We don't display load units/quantities for intermodal shipments
        if (!isIntermodalShipmentType(shipment)) {
            _.each(loads, (load) => {
                // Full truckload
                if (load.quantity < 999) {
                    previewComponent.push(React.createElement("div", { key: load.type, className: "mb-2" },
                        React.createElement(LoadDimensionFormatter, { quantity: load.quantity, type: load.type, length: load.length, width: load.width, height: load.height, dimensional_units: load.dimensional_units })));
                }
            });
        }
        if (loads &&
            loads.length > 1 &&
            totalPalletSpaces &&
            totalPalletSpaces > 0 &&
            SessionStore.isAdmin()) {
            previewComponent.push(React.createElement("div", { key: "total-pallet-spaces" }, t('TOTAL_PALLET_SPACE_WITH_VALUE', {
                totalPalletSpace: totalPalletSpaces,
            })));
        }
        return previewComponent;
    }
    getNextAction() {
        const { t, shipment, stop, completeActionDisabled } = this.props;
        const latest = getLatestStatus(stop);
        const hasArrivalInfoTag = shipment.shipper &&
            shipment.shipper.tags.includes('requires_arrival_info');
        const hasDrayageTrailerMovesTag = shipperHasTag(shipment, 'drayage_trailer_moves', shipment.shipment_type === 'drayage');
        const hasTrailer = trailerExists(stop.equipments || []);
        const arrival = hasArrivalInfoTag &&
            hasNotArrived(latest, shipment.shipper, completeActionDisabled, this.handleStopArrived);
        if (arrival)
            return arrival;
        const { stop: { sequence }, } = this.props;
        let { stop: { action }, } = this.props;
        const isCarrierMandatoryStopDocumentsEnabled = featureEnabled(CONFIGURED_FEATURES.CARRIER_MANDATORY_STOP_DOCUMENTS);
        const documentationRequired = isCarrierMandatoryStopDocumentsEnabled
            ? shouldEnterProvideDocumentationWithCombinedDocsCheck(stop)
            : shouldEnterProvideDocumentation(shipment, stop);
        const showAddEquipButton = shouldAddEquipment(shipment, stop);
        if (showAddEquipButton) {
            action = 'Add Equipment';
        }
        if (action === 'deliver') {
            action = 'delivery';
        }
        const actionText = documentationRequired || humanize(action);
        const shouldHaveTrailer = hasDrayageTrailerMovesTag && !hasTrailer;
        return (React.createElement(Button, { className: "w-full bg-green-cm hover:bg-green-cm", id: `${action}_stop_${sequence}`, disabled: !showAddEquipButton && (completeActionDisabled || shouldHaveTrailer), onClick: showAddEquipButton
                ? () => this.handleEquipmentClick()
                : () => this.handleStopComplete() }, titleize(t(sluggify(actionText)))));
    }
    handleStopComplete() {
        this.props.onShowStopCompleteModal(this.props.stop);
    }
    showEditIcon(sequence, type) {
        return (this.state.hoveredStop === sequence && this.state.hoveredType === type);
    }
    renderAppointmentWindow() {
        const { stop, shipment: { shipment_type }, } = this.props;
        return (React.createElement("div", { className: "col-span-2", onMouseEnter: () => this.onMouseOver(stop.sequence, 'windows'), onMouseLeave: () => this.onMouseOut() },
            React.createElement(StopAppointmentWindow, { stop: stop, shipment_type: shipment_type, editable: true, onEditAppointmentWindow: () => {
                    this.props.onShowEditTimeWindowModal(stop);
                } })));
    }
    renderTimeline() {
        const { stop: { timeline }, t, } = this.props;
        const flattened = Object.keys(timeline).map((key) => (Object.assign({ key }, timeline[key])));
        const sortedTimeline = _.sortBy(flattened, (t) => { var _a; return (_a = t === null || t === void 0 ? void 0 : t.status) === null || _a === void 0 ? void 0 : _a.code; });
        const items = sortedTimeline.map((timelineItem, i) => (React.createElement(TableRow, { key: i },
            React.createElement(TableCell, null, timelineItem.status.message),
            React.createElement(TableCell, null, moment
                .tz(timelineItem.at, getTimezone(this.props.stop))
                .format('L LT z')),
            React.createElement(TableCell, null, hasMetaDate(timelineItem)
                ? moment
                    .tz(timelineItem.meta.date, getTimezone(this.props.stop))
                    .format('L LT z')
                : null))));
        return (React.createElement("div", { className: "overflow-x-auto" },
            React.createElement(Table, { className: "" },
                React.createElement(TableHeader, null,
                    React.createElement(TableRow, null,
                        React.createElement(TableHead, null, t('STATUS')),
                        React.createElement(TableHead, null, t('TIMESTAMP')),
                        React.createElement(TableHead, null, t('ACTUAL')))),
                React.createElement(TableBody, null, items))));
    }
    renderTimelineAuditHistory() {
        const { t, audits } = this.props;
        if (!(audits === null || audits === void 0 ? void 0 : audits.length)) {
            return React.createElement("p", null, t('NO_DATA_FOUND'));
        }
        const items = (audits || []).map((audit, i) => {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            return (React.createElement(TableRow, { key: i },
                React.createElement(TableCell, null, titleize(t(sluggify((_a = audit === null || audit === void 0 ? void 0 : audit.meta) === null || _a === void 0 ? void 0 : _a.stop_status))) || ''),
                React.createElement(TableCell, null, moment
                    .tz(audit === null || audit === void 0 ? void 0 : audit.timestamp, getTimezone(this.props.stop))
                    .format('L LT z')),
                React.createElement(TableCell, null, hasMetaDate(audit)
                    ? moment
                        .tz((_b = audit === null || audit === void 0 ? void 0 : audit.meta) === null || _b === void 0 ? void 0 : _b.date, getTimezone(this.props.stop))
                        .format('L LT z')
                    : null),
                React.createElement(TableCell, null,
                    React.createElement("div", { className: "flex items-center space-x-1" },
                        React.createElement("span", null, t(`${sluggify(audit === null || audit === void 0 ? void 0 : audit.event)}_EVENT_MESSAGE`) || ''),
                        ((_c = audit === null || audit === void 0 ? void 0 : audit.meta) === null || _c === void 0 ? void 0 : _c.request_source) && (React.createElement("div", { className: "w-fit" },
                            React.createElement(Tooltip, { content: getStopTooltip((_d = audit === null || audit === void 0 ? void 0 : audit.meta) === null || _d === void 0 ? void 0 : _d.request_source) },
                                React.createElement(InfoIcon, null)))))),
                React.createElement(TableCell, null, ((_e = audit === null || audit === void 0 ? void 0 : audit.meta) === null || _e === void 0 ? void 0 : _e.reason_type) || ''),
                React.createElement(TableCell, null, ((_f = audit === null || audit === void 0 ? void 0 : audit.meta) === null || _f === void 0 ? void 0 : _f.reason_code) || ''),
                React.createElement(TableCell, null, ((_g = audit === null || audit === void 0 ? void 0 : audit.meta) === null || _g === void 0 ? void 0 : _g.reason_description) || ''),
                React.createElement(TableCell, null, ((_h = audit === null || audit === void 0 ? void 0 : audit.meta) === null || _h === void 0 ? void 0 : _h.message) || '')));
        });
        return (React.createElement("div", { className: "overflow-x-auto" },
            React.createElement(Table, { className: "" },
                React.createElement(TableHeader, null,
                    React.createElement(TableRow, null,
                        React.createElement(TableHead, null, t('STATUS')),
                        React.createElement(TableHead, null, t('TIMESTAMP')),
                        React.createElement(TableHead, null, t('ACTUAL')),
                        React.createElement(TableHead, null, t('STOP_ACTION')),
                        React.createElement(TableHead, null, t('REASON_TYPE')),
                        React.createElement(TableHead, null, t('REASON_CODE')),
                        React.createElement(TableHead, null, t('REASON_DESCRIPTION')),
                        React.createElement(TableHead, null, t('COMMENT')))),
                React.createElement(TableBody, null, items))));
    }
    renderReferenceNumbers() {
        const { stop: { reference_numbers }, t, } = this.props;
        if (!reference_numbers || reference_numbers.length === 0) {
            return React.createElement("p", null, t('NO_REFERENCE_NUMBERS'));
        }
        const refNumList = reference_numbers.map((refNum, i) => (React.createElement(TableRow, { key: i },
            React.createElement(TableCell, null, humanize(refNum.name)),
            React.createElement(TableCell, null, refNum.value))));
        return (React.createElement(Table, { className: "" },
            React.createElement(TableHeader, null,
                React.createElement(TableRow, null,
                    React.createElement(TableHead, null, "Name"),
                    React.createElement(TableHead, null, "Value"))),
            React.createElement(TableBody, null, refNumList)));
    }
    componentDidUpdate(prevProps) {
        var _a, _b;
        if (prevProps.stop &&
            this.props.stop &&
            (prevProps.stop.status !== this.props.stop.status ||
                prevProps.stop.proof_of_completion !==
                    this.props.stop.proof_of_completion)) {
            (_b = (_a = this.state).documentsRefetch) === null || _b === void 0 ? void 0 : _b.call(_a);
        }
    }
    render() {
        var _a, _b;
        const { shipment: { shipper_accessorials }, stop, lastStop, selectedForSplit, t, yardName, } = this.props;
        if (!stop)
            return t('LOADING');
        let color = '#38495C';
        const shipmenType = this.props.shipment.shipment_type;
        if (stop.canceled || stop.attempt)
            color = '#d9534f';
        const { stop: { timeline }, } = this.props;
        const flattened = Object.keys(timeline).map((key) => (Object.assign({ key }, timeline[key])));
        const sortedTimeline = filterTimelineItems(_.sortBy(flattened, (t) => { var _a; return (_a = t === null || t === void 0 ? void 0 : t.status) === null || _a === void 0 ? void 0 : _a.code; }));
        const stopIntent = humanize(t(sluggify(getDisplayAction(stop))));
        return (React.createElement(React.Fragment, null,
            React.createElement("div", { className: "py-5 sm:py-6" },
                React.createElement("div", { className: "grid grid-cols-12 gap-4 min-w-[1100px]" },
                    React.createElement("div", { className: "flex col-span-2 2xl:col-span-1", onClick: () => this.props.onStopSelect(stop) },
                        this.props.children,
                        React.createElement(Node, { className: "", color: color, sequence: stop.sequence, selectedForSplit: selectedForSplit, lastStop: lastStop }),
                        React.createElement("div", { className: "ml-2 mt-2" }, stopIntent)),
                    React.createElement("div", { className: "col-span-2 2xl:col-span-3", onMouseEnter: () => this.onMouseOver(stop.sequence, 'location'), onMouseLeave: () => this.onMouseOut() },
                        React.createElement("div", { className: "flex items-start gap-1 min-w-0" },
                            React.createElement("div", { className: "flex-1 min-w-0" },
                                React.createElement(DescriptionListItem, { key: "address", label: yardName || humanize(getStopName(stop.location)), className: "mb-4" }, getStopAddress(stop.location)),
                                React.createElement("div", null, stop.contacts &&
                                    stop.contacts.map((contact, i) => (React.createElement("div", { key: i, className: "text-sm" }, getContact(contact))))),
                                React.createElement("div", { className: "mt-2" }, stop.off_site && (React.createElement(Badge, { color: "blue" }, t('OFFSITE_STOP'))))),
                            React.createElement(Button, { size: "icon", variant: "icon", onClick: () => this.props.onShowEditLocationIntentModal(stop) },
                                React.createElement(PencilIcon, { className: "w-4 h-4 text-gray-500" })))),
                    this.renderAppointmentWindow(),
                    React.createElement("div", { className: "col-span-2 overflow-hidden", onMouseEnter: () => this.onMouseOver(stop.sequence, 'load'), onMouseLeave: () => this.onMouseOut() },
                        React.createElement("div", { className: "flex items-start gap-1 min-w-0" },
                            React.createElement("div", { className: "flex-1 min-w-0" }, this.getLoadPreview(this.props.shipment)),
                            React.createElement("div", { style: {
                                    display: this.showEditIcon(stop.sequence, 'load'),
                                } },
                                React.createElement(Button, { variant: "icon", size: "icon", onClick: () => {
                                        if (isDrayageOrRailShipment(this.props.shipment)) {
                                            this.props.onShowEditEquipmentModal(stop);
                                        }
                                        else {
                                            this.props.onShowEditLoadsModal(stop);
                                        }
                                    } },
                                    React.createElement(PencilIcon, { className: "w-4 h-4 text-gray-500", style: {
                                            display: this.hasShorthaulInvoicingData(this.props)
                                                ? 'none'
                                                : 'auto',
                                        } }))))),
                    React.createElement("div", { className: "col-span-2" }, ((_a = sortedTimeline === null || sortedTimeline === void 0 ? void 0 : sortedTimeline.length) !== null && _a !== void 0 ? _a : 0) === 0 ? (React.createElement("span", null, "\u00A0")) : (React.createElement(Timeline, null, (sortedTimeline !== null && sortedTimeline !== void 0 ? sortedTimeline : []).map((timelineItem, i) => (React.createElement(TimelineEvent, { key: i, className: "space-y-2" },
                        React.createElement(TimelineDot, null),
                        React.createElement(DescriptionListItem, { label: timelineItem.status.message }, hasMetaDate(timelineItem) && (React.createElement("p", { className: "mt-2 text-gray-700 text-sm" }, moment
                            .tz(timelineItem.meta.date, getTimezone(this.props.stop))
                            .format('L LT z')))))))))),
                    React.createElement("div", { className: "col-span-2" },
                        React.createElement("div", { className: "w-44" },
                            this.getNextAction(),
                            React.createElement(DropdownMenu, { id: "actions", className: "" },
                                React.createElement(DropdownMenuTrigger, { asChild: true, className: "w-full mt-4", disabled: this.props.actionsDisabled },
                                    React.createElement(Button, { variant: "outline", className: "w-full" }, t('MANAGE'))),
                                React.createElement(DropdownMenuContent, { className: "w-[var(--radix-dropdown-menu-trigger-width)]" },
                                    React.createElement(DropdownMenuItem, { onSelect: () => this.props.onActionSelect('unstarted') }, t('RE_OPEN')),
                                    React.createElement(DropdownMenuItem, { disabled: isDrayageOrRailShipment(this.props.shipment), onSelect: () => this.props.onActionSelect('attempt') }, t('ATTEMPT')),
                                    isDrayageOrRailShipment(this.props.shipment) && (React.createElement(DropdownMenuItem, { onSelect: () => this.props.onActionSelect('canceled') }, t('CANCEL'))),
                                    stop.action === 'pickup' &&
                                        isDrayageOrRailShipment(this.props.shipment) && (React.createElement(DropdownMenuItem, { onSelect: () => this.props.onActionSelect('add_stop_off') }, t('ADD_STOP_OFF'))))))))),
            React.createElement(Disclosure.Panel, null,
                React.createElement("div", null,
                    React.createElement("div", { className: "mt-4 lg:grid lg:grid-cols-12 lg:gap-4 space-x-4" },
                        React.createElement("div", { className: "lg:col-span-4" },
                            React.createElement("div", { className: "flex items-center mb-4 space-x-2" },
                                React.createElement("h4", { className: "my-0" }, t('DOCUMENTS')),
                                React.createElement(Tooltip, { content: TOOLTIPS.documents },
                                    React.createElement(InfoIcon, null))),
                            React.createElement(Documents, { shipment: stop, isStop: true, stopShipmentId: (_b = this.props.shipment) === null || _b === void 0 ? void 0 : _b._id, showDropzone: false, onDelete: (document) => {
                                    console.log('Deleted Document: ', document);
                                }, onUpload: (shipment, type, documents) => {
                                    console.log('Uploaded Documents: ', documents);
                                }, refetchRef: (documentsRefetch) => this.setDocumentsRefetchRef(documentsRefetch) })),
                        shipmenType === 'drayage' ? null : (React.createElement("div", { className: "lg:col-span-4" },
                            React.createElement("div", { className: "flex items-center mb-4 space-x-2" },
                                React.createElement("h4", { className: "my-0" }, t('ACCESSORIAL', { count: 3 })),
                                React.createElement(Tooltip, { content: TOOLTIPS.accessorials },
                                    React.createElement(InfoIcon, null))),
                            React.createElement(Accessorials, { accessorials: shipper_accessorials }))),
                        React.createElement("div", { className: "lg:col-span-4", onMouseEnter: () => {
                                this.onMouseOver(this.props.stop.sequence, 'reference_numbers');
                            }, onMouseLeave: () => this.onMouseOut() },
                            React.createElement("div", { className: "flex space-x-4" },
                                React.createElement("div", { className: "flex-1" },
                                    React.createElement("div", { className: "flex items-center mb-4 space-x-2" },
                                        React.createElement("h4", { className: "my-0" },
                                            t('REFERENCE_NUMBERS'),
                                            " "),
                                        React.createElement(Tooltip, { content: TOOLTIPS.references },
                                            React.createElement(InfoIcon, null))),
                                    this.renderReferenceNumbers()),
                                React.createElement("div", { style: {
                                        display: this.showEditIcon(stop.sequence, 'reference_numbers')
                                            ? ''
                                            : 'none',
                                    } },
                                    React.createElement(Button, { variant: "icon", size: "icon", onClick: () => {
                                            this.props.onShowEditReferenceNumbersModal(stop);
                                        } },
                                        React.createElement(PencilIcon, { className: "w-4 h-4 text-gray-500" }))))),
                        React.createElement("div", { className: "lg:col-span-4" },
                            React.createElement("h4", { className: "my-0 mb-4" }, t('TIMELINE')),
                            this.renderTimeline()),
                        React.createElement("div", { className: "col-span-12 " },
                            React.createElement("div", { className: "flex items-center" },
                                React.createElement("h4", { className: "my-4 mb-4 mr-2" }, t('STOP_TIMELINE_HISTORY')),
                                React.createElement(CircleButton, { size: "small", onClick: () => {
                                        var _a, _b;
                                        (_b = (_a = this.props).refreshStopsAudit) === null || _b === void 0 ? void 0 : _b.call(_a);
                                    } },
                                    React.createElement(RefreshIcon, { className: "w-5 h-5" }))),
                            this.renderTimelineAuditHistory()))))));
    }
}
Stop.propTypes = {
    children: PropTypes.any,
    stop: PropTypes.shape({
        sequence: PropTypes.number.isRequired,
        action: PropTypes.string.isRequired,
        location: PropTypes.array.isRequired,
        contacts: PropTypes.array,
        window_start: PropTypes.string,
        window_end: PropTypes.string,
        timeline: PropTypes.object,
        canceled: PropTypes.bool,
        attempt: PropTypes.bool,
        reference_numbers: PropTypes.array,
        status: PropTypes.string,
        complete: PropTypes.bool,
        equipments: PropTypes.array,
        loads: PropTypes.array,
        full_truckload: PropTypes.bool,
    }).isRequired,
    completeActionDisabled: PropTypes.bool,
    actionsDisabled: PropTypes.bool,
    lastStop: PropTypes.bool,
    shipment: PropTypes.object,
    onActionSelect: PropTypes.func.isRequired,
    onShowEditLocationIntentModal: PropTypes.func.isRequired,
    onShowEditLoadsModal: PropTypes.func.isRequired,
    onShowEditReferenceNumbersModal: PropTypes.func.isRequired,
    onShowEditTimeWindowModal: PropTypes.func.isRequired,
    onShowEditEquipmentModal: PropTypes.func.isRequired,
    onShowStopCompleteModal: PropTypes.func.isRequired,
    onShowStopDocumentPreviewModal: PropTypes.func.isRequired,
    onShowStopArrivedModal: PropTypes.func.isRequired,
    expanded: PropTypes.bool.isRequired,
    showTotalWeight: PropTypes.bool,
};
Stop.defaultProps = {
    showTotalWeight: true,
};
export default withTranslation()(Stop);
